import clsx from 'clsx';
import type { ElementType, ReactNode, ComponentPropsWithoutRef } from 'react';

type BaseOwnProps = {
  className?: string;
  children: ReactNode;
};

type PolymorphicProps<E extends ElementType, P> =
  P & { as?: E } & Omit<ComponentPropsWithoutRef<E>, keyof P | 'as'>;

const sectionBase = 'py-16 lg:py-24';
const heroBase = 'py-20 lg:py-32';
const containerBase = 'max-w-7xl mx-auto px-4 md:px-6 lg:px-8';

export function Section<E extends ElementType = 'section'>(
  props: PolymorphicProps<E, BaseOwnProps>,
) {
  const { as, className, children, ...rest } = props;
  const Component = (as ?? 'section') as ElementType;

  return (
    <Component className={clsx(sectionBase, className)} {...rest}>
      {children}
    </Component>
  );
}

export function HeroSection<E extends ElementType = 'section'>(
  props: PolymorphicProps<E, BaseOwnProps>,
) {
  const { as, className, children, ...rest } = props;
  const Component = (as ?? 'section') as ElementType;

  return (
    <Component className={clsx(heroBase, className)} {...rest}>
      {children}
    </Component>
  );
}

export function Container<E extends ElementType = 'div'>(
  props: PolymorphicProps<E, BaseOwnProps>,
) {
  const { as, className, children, ...rest } = props;
  const Component = (as ?? 'div') as ElementType;

  return (
    <Component className={clsx(containerBase, className)} {...rest}>
      {children}
    </Component>
  );
}

type SectionTitleProps = {
  children: ReactNode;
  center?: boolean;
};

export function SectionTitle({ children, center = false }: SectionTitleProps) {
  return (
    <h2
      className={clsx(
        'text-4xl md:text-5xl font-bold text-white mb-6',
        center && 'text-center',
      )}
    >
      {children}
    </h2>
  );
}

export const Divider = () => <div className="my-16 md:my-24" />;
